import { SelectOption } from 'components/Forms';
import { StateAbbrev } from 'utils/states';

export type TimeZone =
  | 'America/New_York'
  | 'America/Chicago'
  | 'America/Denver'
  | 'America/Phoenix'
  | 'America/Los_Angeles'
  | 'America/Anchorage'
  | 'Pacific/Honolulu';

export type ResolvedTimeZone =
  | 'Eastern'
  | 'Central'
  | 'Mountain'
  | 'Mountain (Arizona / No DST Observed)'
  | 'Pacific'
  | 'Alaskan'
  | 'Hawaiian';

export interface TimezoneOption extends SelectOption {
  value: TimeZone;
  shortName: string;
  resolvedName: ResolvedTimeZone;
}

export const timeZoneOptions: TimezoneOption[] = [
  { label: 'Eastern Standard Time (EST)', value: 'America/New_York', shortName: 'EST', resolvedName: 'Eastern' },
  { label: 'Central Standard Time (CST)', value: 'America/Chicago', shortName: 'CST', resolvedName: 'Central' },
  { label: 'Mountain Standard Time (MST)', value: 'America/Denver', shortName: 'MST', resolvedName: 'Mountain' },
  {
    label: 'US Mountain Standard Time (UMST)',
    value: 'America/Phoenix',
    shortName: 'UMST',
    resolvedName: 'Mountain (Arizona / No DST Observed)',
  },
  { label: 'Pacific Standard Time (PST)', value: 'America/Los_Angeles', shortName: 'PST', resolvedName: 'Pacific' },
  { label: 'Alaskan Standard Time (AKST)', value: 'America/Anchorage', shortName: 'AKST', resolvedName: 'Alaskan' },
  { label: 'Hawaiian Standard Time (HST)', value: 'Pacific/Honolulu', shortName: 'HST', resolvedName: 'Hawaiian' },
];

export const DEFAULT_TIMEZONE_OPTION = timeZoneOptions[0];

export const getLocalTimeZone = (): string => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const detectTimeZone = (): TimeZone | undefined => {
  const localTimeZone = getLocalTimeZone();
  return timeZoneOptions.find(({ value }) => value === localTimeZone)?.value;
};

export const getDefaultTimezoneOption = (forcedDefault?: TimezoneOption): TimezoneOption | undefined => {
  if (forcedDefault) {
    return forcedDefault;
  }

  const defaultDetected = detectTimeZone();

  if (defaultDetected) {
    return timeZoneOptions.find(({ value }) => value === defaultDetected);
  }

  return undefined;
};

/** Tries to match a timezone string to our list of allowed timezones */
export const findDefaultTimezoneOption = (timezone: string) => {
  return getDefaultTimezoneOption(timeZoneOptions.find(({ value }) => value === timezone));
};

export const findTimezoneByResolvedName = (resolvedName: string) =>
  timeZoneOptions.find(timezone => timezone.resolvedName === resolvedName);

// Created by AI, might be incorrect, only an approximation
export const stateTimeZonesMap: Record<StateAbbrev, TimeZone> = {
  AL: 'America/Chicago',
  AK: 'America/Anchorage',
  AS: 'Pacific/Honolulu', // American Samoa uses UTC-11, closest equivalent in your type is Pacific/Honolulu
  AZ: 'America/Phoenix',
  AR: 'America/Chicago',
  CA: 'America/Los_Angeles',
  CO: 'America/Denver',
  CT: 'America/New_York',
  DC: 'America/New_York', // Washington, D.C.
  DE: 'America/New_York',
  FM: 'Pacific/Honolulu', // Federated States of Micronesia uses UTC+10 or UTC+11; closest equivalent in your type
  FL: 'America/New_York', // Note: The Florida Panhandle uses 'America/Chicago'
  GA: 'America/New_York',
  GU: 'Pacific/Honolulu', // Guam uses UTC+10, closest equivalent
  HI: 'Pacific/Honolulu',
  ID: 'America/Denver', // Note: Northern Idaho uses 'America/Los_Angeles'
  IL: 'America/Chicago',
  IN: 'America/New_York', // Note: Northwestern and Southwestern Indiana use 'America/Chicago'
  IA: 'America/Chicago',
  KS: 'America/Chicago', // Note: Some western parts use 'America/Denver'
  KY: 'America/New_York', // Note: Western Kentucky uses 'America/Chicago'
  LA: 'America/Chicago',
  ME: 'America/New_York',
  MD: 'America/New_York',
  MA: 'America/New_York',
  MI: 'America/New_York', // Note: The Upper Peninsula uses 'America/Chicago'
  MN: 'America/Chicago',
  MS: 'America/Chicago',
  MO: 'America/Chicago',
  MT: 'America/Denver',
  NE: 'America/Chicago', // Note: Western Nebraska uses 'America/Denver'
  NV: 'America/Los_Angeles', // Note: Some parts use 'America/Denver'
  NH: 'America/New_York',
  NJ: 'America/New_York',
  NM: 'America/Denver',
  NY: 'America/New_York',
  NC: 'America/New_York',
  ND: 'America/Chicago', // Note: Western North Dakota uses 'America/Denver'
  OH: 'America/New_York',
  OK: 'America/Chicago',
  OR: 'America/Los_Angeles', // Note: Most of Malheur County uses 'America/Denver'
  PA: 'America/New_York',
  PR: 'America/New_York', // Puerto Rico uses Atlantic Standard Time (UTC-4), closest equivalent in your type
  PW: 'Pacific/Honolulu', // Palau uses UTC+9, closest equivalent
  RI: 'America/New_York',
  SC: 'America/New_York',
  SD: 'America/Chicago', // Note: Western South Dakota uses 'America/Denver'
  TN: 'America/Chicago', // Note: Eastern Tennessee uses 'America/New_York'
  TX: 'America/Chicago', // Note: El Paso and Hudspeth counties use 'America/Denver'
  UT: 'America/Denver',
  VT: 'America/New_York',
  VA: 'America/New_York',
  VI: 'America/New_York', // U.S. Virgin Islands use Atlantic Standard Time (UTC-4), closest equivalent
  WA: 'America/Los_Angeles',
  WV: 'America/New_York',
  WI: 'America/Chicago',
  WY: 'America/Denver',
  MH: 'Pacific/Honolulu',
  MP: 'Pacific/Honolulu',
};

/** Tries to match a state to a timezone option */
export const findDefaultTimezoneOptionByState = (state: StateAbbrev): TimezoneOption => {
  return timeZoneOptions.find(({ value }) => stateTimeZonesMap[state] === value) as TimezoneOption;
};

export const getTimezoneOptionFromResolved = (resolvedName: ResolvedTimeZone) =>
  timeZoneOptions.find(t => t.resolvedName === resolvedName);
